import type { KeyboardEvent, PointerEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LayoutCanvasGuides } from "./LayoutCanvasGuides";
import { LayoutCanvasTile } from "./LayoutCanvasTile";
import { MIN_TILE_SIZE, SNAP_THRESHOLD_PX } from "./layout-canvas-constants";
import {
  applyKeyboardMove,
  applyKeyboardResize,
  applyMove,
  applyResize,
  isRectOutOfBounds,
  type CanvasRect,
  type Guide,
  type ResizeHandle,
  type SnapOptions,
} from "./layout-canvas-utils";

export type LayoutCanvasEditorProps = {
  width: number;
  height: number;
  positions: CanvasRect[];
  disabled?: boolean;
  selectedIndex: number | null;
  minTileSize?: number;
  gridSize?: number;
  snapEnabled?: boolean;
  maxHeight?: number;
  // An optional layer (e.g. a media preview) rendered behind the tiles,
  // filling the canvas.
  background?: ReactNode;
  // Custom content for each tile (replacing the numbered badge look) —
  // e.g. a live styled preview of what the tile represents. `scale` is the
  // ratio of the canvas's rendered CSS width to its logical `width` prop —
  // content drawn at literal wall pixels inside the tile should be wrapped
  // in `transform: scale(scale)` so it matches what the tile's percentage
  // sizing implies, rather than overflowing/underflowing the tile.
  renderTile?: (index: number, rect: CanvasRect, scale: number) => ReactNode;
  showIndexBadges?: boolean;
  onSelect: (index: number | null) => void;
  onChange: (index: number, rect: CanvasRect) => void;
};

const DEFAULT_MAX_HEIGHT = 720;

type DragState = {
  mode: "move" | "resize";
  handle?: ResizeHandle;
  index: number;
  pointerId: number;
  startClient: { x: number; y: number };
  startRect: CanvasRect;
  box: DOMRect;
  siblings: CanvasRect[];
};

// The canvas is sized in CSS percentages of `width`/`height` rather than a
// fixed pixel box shrunk with `zoom`/`transform: scale()`. That keeps resize
// handles, guide lines, and index badges at a constant screen size — pointer
// coordinates are converted with a single getBoundingClientRect() read at
// gesture start, with no need to track scale for that. The `scale` state
// below exists purely for `renderTile`, whose content (e.g. a wall preview)
// is drawn at literal pixels and needs the ratio to compensate.
export function LayoutCanvasEditor({
  width,
  height,
  positions,
  disabled,
  selectedIndex,
  minTileSize = MIN_TILE_SIZE,
  gridSize = 0,
  snapEnabled = true,
  maxHeight = DEFAULT_MAX_HEIGHT,
  background,
  renderTile,
  showIndexBadges = true,
  onSelect,
  onChange,
}: LayoutCanvasEditorProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [previewRect, setPreviewRect] = useState<{
    index: number;
    rect: CanvasRect;
  } | null>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [scale, setScale] = useState<number>(1);

  useEffect(
    function effectOnCanvasResize() {
      const el = canvasRef.current;
      if (!el || !width) {
        return;
      }

      const observer = new ResizeObserver(() => {
        setScale(el.clientWidth / width);
      });
      observer.observe(el);
      setScale(el.clientWidth / width);

      return () => observer.disconnect();
    },
    [width],
  );

  const displayedPositions = useMemo(
    () =>
      positions.map((rect, index) =>
        previewRect?.index === index ? previewRect.rect : rect,
      ),
    [positions, previewRect],
  );

  const beginDrag = useCallback(
    (
      index: number,
      event: PointerEvent<HTMLDivElement>,
      mode: "move" | "resize",
      handle?: ResizeHandle,
    ) => {
      if (disabled || !canvasRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);

      onSelect(index);
      canvasRef.current.focus();

      dragRef.current = {
        mode,
        handle,
        index,
        pointerId: event.pointerId,
        startClient: { x: event.clientX, y: event.clientY },
        startRect: positions[index],
        box: canvasRef.current.getBoundingClientRect(),
        siblings: positions.filter((_, i) => i !== index),
      };
    },
    [disabled, onSelect, positions],
  );

  const onPointerDownTile = useCallback(
    (index: number, event: PointerEvent<HTMLDivElement>) =>
      beginDrag(index, event, "move"),
    [beginDrag],
  );

  const onPointerDownHandle = useCallback(
    (
      index: number,
      handle: ResizeHandle,
      event: PointerEvent<HTMLDivElement>,
    ) => beginDrag(index, event, "resize", handle),
    [beginDrag],
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      const dx =
        ((event.clientX - drag.startClient.x) / drag.box.width) * width;
      const dy =
        ((event.clientY - drag.startClient.y) / drag.box.height) * height;

      const snapOptions: SnapOptions = {
        canvasWidth: width,
        canvasHeight: height,
        siblings: drag.siblings,
        gridSize,
        snapEnabled: snapEnabled && !event.altKey,
        thresholdCanvasPx: (SNAP_THRESHOLD_PX / drag.box.width) * width,
      };

      const result =
        drag.mode === "move"
          ? applyMove(drag.startRect, dx, dy, snapOptions)
          : applyResize(
              drag.startRect,
              drag.handle as ResizeHandle,
              dx,
              dy,
              snapOptions,
              minTileSize,
            );

      setPreviewRect({ index: drag.index, rect: result.rect });
      setGuides(result.guides);
    },
    [width, height, gridSize, snapEnabled, minTileSize],
  );

  const clearDrag = useCallback(() => {
    dragRef.current = null;
    setPreviewRect(null);
    setGuides([]);
  }, []);

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      if (previewRect && previewRect.index === drag.index) {
        onChange(drag.index, previewRect.rect);
      }

      clearDrag();
    },
    [clearDrag, onChange, previewRect],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled || selectedIndex === null || !positions[selectedIndex]) {
        return;
      }

      if (event.key === "Escape") {
        clearDrag();
        onSelect(null);
        return;
      }

      const arrowKeys = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
      ] as const;
      if (!(arrowKeys as readonly string[]).includes(event.key)) {
        return;
      }

      // preventDefault is what stops arrow keys from scrolling the
      // surrounding page/drawer instead of nudging the tile.
      event.preventDefault();

      const step = event.shiftKey ? 10 : 1;
      const rect = positions[selectedIndex];
      const dx =
        event.key === "ArrowLeft"
          ? -step
          : event.key === "ArrowRight"
            ? step
            : 0;
      const dy =
        event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;

      const nextRect = event.altKey
        ? applyKeyboardResize(rect, dx, dy, width, height, minTileSize)
        : applyKeyboardMove(rect, dx, dy, width, height);

      onChange(selectedIndex, nextRect);
    },
    [
      clearDrag,
      disabled,
      selectedIndex,
      positions,
      width,
      height,
      minTileSize,
      onChange,
      onSelect,
    ],
  );

  if (!width || !height) {
    return null;
  }

  return (
    <div
      ref={canvasRef}
      tabIndex={disabled ? -1 : 0}
      role="application"
      aria-label="Custom layout canvas"
      className="relative bg-slate-100 border border-slate-300 rounded outline-none focus-visible:ring-2 focus-visible:ring-blue-500 select-none"
      style={{
        // Width is the only sized dimension; height is purely derived via
        // aspect-ratio. That's what makes the shrink-to-fit correct — a
        // fixed `width: 100%` alongside `max-height` would clamp height
        // without renegotiating width, silently breaking the aspect ratio
        // instead of shrinking the whole box down proportionally.
        width: `min(100%, calc(${maxHeight}px * ${width} / ${height}))`,
        aspectRatio: `${width} / ${height}`,
      }}
      onPointerDown={() => onSelect(null)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={clearDrag}
      onKeyDown={onKeyDown}
    >
      {background && (
        <div className="absolute inset-0 overflow-hidden rounded pointer-events-none">
          {background}
        </div>
      )}
      {displayedPositions.map((rect, index) => (
        <LayoutCanvasTile
          key={index}
          index={index}
          rect={rect}
          canvasWidth={width}
          canvasHeight={height}
          selected={selectedIndex === index}
          disabled={disabled}
          outOfBounds={isRectOutOfBounds(rect, width, height)}
          showIndexBadge={showIndexBadges}
          onPointerDownTile={onPointerDownTile}
          onPointerDownHandle={onPointerDownHandle}
        >
          {renderTile?.(index, rect, scale)}
        </LayoutCanvasTile>
      ))}
      <LayoutCanvasGuides
        guides={guides}
        canvasWidth={width}
        canvasHeight={height}
      />
    </div>
  );
}
